import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import Markdown from 'react-native-markdown-display';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from '../../services/chatApi';
import { useTheme } from '../../contexts/ThemeContext';
import { CitationChip } from './CitationChip';
import { ResearchPending } from './ResearchPending';
import { createMarkdownStyles } from './markdownStyles';
import { chatMarkdownRules } from './markdownRules';

export function MessageBubble({
  message,
  resolvedJobIds,
}: {
  message: ChatMessage;
  resolvedJobIds?: Set<string>;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  const bgColor = isUser ? theme.colors.primary : theme.colors.surfaceSecondary;
  const fgColor = isUser ? theme.colors.textInverse : theme.colors.text;

  // Assistant replies are markdown; render them formatted. User messages are
  // plain text (no markdown), so keep them in a bare <Text>.
  const mdStyles = useMemo(() => createMarkdownStyles(theme, fgColor), [theme, fgColor]);

  // Structured turns (spec §4.1): commands, sheet edits, and save receipts are
  // replay data, not conversation — collapse each to one muted line, no bubble.
  // The persisted contents are self-labeled ("📐 Build thesis", "✎ Card edited
  // — …", "✓ Thesis saved"), so the content renders alone: a label prefix here
  // doubled every glyph.
  const meta = (message.metadata ?? null) as Record<string, unknown> | null;
  // The wizard advance marker (tp-wizard) is the one UNSELF-LABELED command:
  // its persisted content is the bare "✅" glyph, so the compact line renders
  // the localized confirmedStage label instead of the content.
  const commandMeta = meta?.command as { command?: string } | null | undefined;
  const isAdvanceMarker = commandMeta?.command === 'target_price_advance';
  if (meta?.command || meta?.thesis_edit || message.role === 'system') {
    return (
      <View style={styles.compactRow}>
        <Text
          style={[
            styles.compactText,
            { color: theme.colors.textTertiary, fontSize: theme.typography.fontSizeSm },
          ]}
        >
          {isAdvanceMarker
            ? t('chat.targetPrice.confirmedStage', { defaultValue: 'Confirmed — moving on' })
            : message.content}
        </Text>
      </View>
    );
  }

  // Show ResearchPending only if the job is not resolved
  const showPending =
    message.role === 'assistant' &&
    message.job_id &&
    (!message.citations?.length || !resolvedJobIds?.has(message.job_id));

  return (
    <View style={[styles.row, { justifyContent: isUser ? 'flex-end' : 'flex-start' }]}>
      <View style={[styles.bubble, { backgroundColor: bgColor }]}>
        {isUser ? (
          <Text style={{ color: fgColor, fontSize: theme.typography.fontSizeMd }}>
            {message.content}
          </Text>
        ) : (
          <Markdown style={mdStyles} rules={chatMarkdownRules}>
            {message.content || ''}
          </Markdown>
        )}
        {showPending && <ResearchPending />}
        {message.citations && message.citations.length > 0 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chips}>
            {message.citations.map((c, i) => (
              <CitationChip key={i} citation={c} />
            ))}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', marginVertical: 4, paddingHorizontal: 12 },
  bubble: {
    maxWidth: '80%',
    padding: 10,
    borderRadius: 12,
    // maxWidth caps this box but not its descendants: a wide table row has a
    // larger minimum content width and paints straight past the right edge of a
    // portrait screen. Shrink below content width and clip to the rounded box.
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  tier: { fontSize: 10, marginTop: 4, opacity: 0.7 },
  chips: { marginTop: 8 },
  compactRow: { alignItems: 'center', paddingVertical: 2, paddingHorizontal: 12 },
  compactText: { flexShrink: 1, textAlign: 'center' },
});
